// eslint-disable-next-line react-hooks/exhaustive-deps
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { connect } from "react-redux";
import CircularDonutChart from "../../../component/CircularDonutChart";
import PerformanceMetrics from "../../../component/PerformanceMetrics";
import { howDoingData } from "./SnapshotData";
import { NextCleaningSection } from "./NextCleaningSection";
import fetchData from "../../../axios/fetchData";
import { telemetry } from "../../../axios/serverUrl";
import { useEffect, useState } from "react";
import { activeSnapCoil, onlyActiveCoil } from "../../../redux/actions/coilAction";
import CustomLoader from "../../../component/Loader";
import { Tooltip as ReactTooltip } from "react-tooltip";
import SmartCoilImage from "../../../component/SmartCoilImage/SmartCoilImage";
import { Tooltip } from "../../../utils/Tooltip";
import {
  getDescriptionForState,
  getFullLabel,
  getLabel,
  getLabelForState,
  getSmartCoilColor,
  OPTIMIZING_STATE,
  RUNNING_STATE
} from "../../../utils/device";
import {getSelectedSharedAttributes} from "../../../axios/deviceApi";
import {usePageTracking} from "../../../analytics/analytics";
import SelectComponent from "../../../component/Input/SelectComponent";

function getSelectedDeviceTitle(selectedDevice) {
  return (<span className="breadcrumbs-subtitle" title={getFullLabel(selectedDevice)}>{getLabel(selectedDevice)}</span>);
}

function getStatus(smartCoilState) {
  if (smartCoilState == null || smartCoilState === RUNNING_STATE) {
    return <></>;
  }
  return <div>
    <h4>
      Status - {getLabelForState(smartCoilState)}
      <span
          className="info-icon ml-2"
          type="button"
          data-tooltip-id={"smart-coil-state-tooltip"}
      >
      <span className="p-button-icon pi pi-info-circle"></span>
    </span>
    </h4>
    <ReactTooltip
        id="smart-coil-state-tooltip"
        place="right-start"
        style={{
          zIndex: 9999,
          width: "200px",
          background: "black",
          marginTop: "-10px",
          marginLeft: "10px",
        }}
        content={getDescriptionForState(smartCoilState)}
    />
  </div>
;
}

const Snapshot = ({
  activeList,
      activeSnapList,
      buildingList,
      relationList,
      onlyActiveCoil,
      activeSnapCoil,
}) => {
  usePageTracking();
  const navigator = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [isPerfMetricsLoading, setPerfMetricsLoading] = useState(false);
  const [recentCoilActiveList, setRecentCoilActiveList] = useState([]);
  const [activeBuildingName, setActiveBuildingName] = useState("");

  const selectedDevice = activeList?.length === 1 ? activeList[0] : undefined;

  // Snapshot's own selection, used only to decide which of the compared coils to
  // keep. Compared by id, not object identity, because the reducers rebuild these
  // arrays on every dispatch.
  const snapDeviceId = activeSnapList?.find((item) => item?.active)?.id?.id;
  const activeIds = activeList?.map((coil) => coil.id.id).join(",") ?? "";

  const [selectedMode, setSelectedMode] = useState('cooling');

  if (!JSON.parse(localStorage.getItem("userDetail"))?.email) {
    navigator("/login");
  }

  // Arriving from a chart page, up to 3 coils can be active. Collapse the selection
  // to exactly one
  useEffect(() => {
    if (!activeList?.length) return; // devices not loaded yet (fresh login)
    const target =
      activeList.find((coil) => coil.id.id === snapDeviceId) || activeList[0];
    if (activeList.length !== 1) {
      onlyActiveCoil(target.id); // first, so ACTIVE_SNAP_COIL sees the new coilList
    }
    if (snapDeviceId !== target.id.id) {
      activeSnapCoil(target.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIds, snapDeviceId]);

  useEffect(() => {
    if (selectedDevice) {
      getFetchChartData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice]);


  const getMonthsToNextCleaning = () => {
    return recentCoilActiveList?.months_to_next_cleaning !== undefined && recentCoilActiveList?.months_to_next_cleaning !== null
      ? Math.round(recentCoilActiveList?.months_to_next_cleaning) : undefined;
  }

  const getFetchChartData = async () => {
    setLoading(true);

    if (!selectedDevice) {
      setLoading(false);
      return;
    }

    const telemetryData = await fetchData(
      "GET",
      telemetry +
        "/" +
        selectedDevice?.id.entityType +
        "/" +
        selectedDevice?.id.id +
        "/values/timeseries?keys=clean_serv,co2_sav,totcostsav_ener,totsav_ener,cc_lat_cal,cc_lat,cc_lat_avg,enercostpen_avg_pre,enercostpen_sum" +
        "&limit=100&agg=MAX&orderBy=ASC",
      { "X-Authorization": "Bearer " + localStorage.getItem("token") },
      {}
    );

    const eventsSince = new Date();
    eventsSince.setFullYear(eventsSince.getFullYear() - 3)
    const events = await fetchData(
        "GET",
        telemetry +
        "/" +
        selectedDevice?.id.entityType +
        "/" +
        selectedDevice?.id.id +
        "/values/timeseries?keys=ahu_event" +
        "&startTs=" + eventsSince.getTime() +
        "&endTs=" + new Date().getTime() +
        "&limit=10&orderBy=DESC",
        { "X-Authorization": "Bearer " + localStorage.getItem("token") },
        {}
    ).then((response) => {
      return response.ahu_event
    });

    const attributes = await getSelectedSharedAttributes(selectedDevice.id);

    if (telemetryData) {
      const monthsCount = (timestamp) => {
        if (timestamp) {
          const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
          const firstDate = new Date(timestamp);
          const secondDate = new Date();
          const diffDays = Math.round(
            Math.abs((firstDate - secondDate) / oneDay)
          );
          var months = Math.floor(diffDays / 30);
          return months;
        }
      };
      const co2SavValue = telemetryData?.co2_sav ? telemetryData.co2_sav[0].value : 0;
      const energySavingValue = telemetryData?.totsav_ener
        ? telemetryData.totsav_ener[0].value
        : 0;
      const costSavingValue = telemetryData?.totcostsav_ener
        ? telemetryData.totcostsav_ener[0].value
        : 0;
      const monthsToNextCleaning = telemetryData?.clean_serv
        ? telemetryData.clean_serv[0].value
        : undefined;
      const enercostpenSum = telemetryData?.enercostpen_sum
        ? telemetryData.enercostpen_sum[0].value
        : undefined;
      const enercostpenAvgPre = telemetryData?.enercostpen_avg_pre
        ? telemetryData.enercostpen_avg_pre[0].value
        : undefined;

      const timestampOfLastCleaning = events?.find(obj => obj.value === "Coil_Cleaned")?.ts;

      const smartCoilState = attributes['smartcoil_state'];
      const coilType = attributes['coil_type'];
      const foulingLevel = attributes['flvl'];

      let newSmartCoilData =
        {
          id: selectedDevice?.id.id,
          name: selectedDevice?.label || selectedDevice?.name,
          smartcoil_state: smartCoilState,
          coil_type: coilType,
          events: events,
        };

      if (smartCoilState === OPTIMIZING_STATE) {
        newSmartCoilData = {
          ...newSmartCoilData,
          months_since_last_cleaning: monthsCount(timestampOfLastCleaning),
          fouling_level: 0,
        }
      }

      if (smartCoilState === RUNNING_STATE) {
        newSmartCoilData =
            {
              ...newSmartCoilData,
              months_since_last_cleaning: monthsCount(timestampOfLastCleaning),
              fouling_level: Math.round(foulingLevel),
              carbon_offset: co2SavValue,
              carbon_offset_per: 5,
              grade: "--",
              energy_saving: energySavingValue,
              energy_saving_per: 4,
              cost_saving: costSavingValue,
              cost_saving_per: 8,
              months_to_next_cleaning: monthsToNextCleaning,
              enercostpenAvgPre: enercostpenAvgPre,
              enercostpenSum: enercostpenSum,
            };
      }
      setLoading(false);
      setRecentCoilActiveList(newSmartCoilData);

      const relatedBuildingId = relationList.filter(
        (coil) => coil.CoilId === newSmartCoilData.id
      )[0]?.BuildingId;
      setActiveBuildingName(
        buildingList.filter(
          (building) => building.id.id === relatedBuildingId
        )[0]?.name
      );
    } else if (telemetryData?.errorCode === 11) {
      navigator("/login");
      localStorage.clear();
    } else {
      setLoading(false);
    }
  };

  return (
    <div className="layout-box ">
      <div style={{ overflow: "auto" }}>
        <div style={{ flex: "0 0 auto" }} className="">
          <div className="d-flex justify-content-between align-items-center fouling-metrics-title mb-3">
            <h2 className="snapshot-title mb-0">Snapshot</h2>
            <span className="w-md-25 pe-1">
                <SelectComponent
                  data={[
                      { label: "Cooling", value: "cooling" },
                      ...(['heating', 'dual_temp', 'HRC'].includes(recentCoilActiveList.coil_type)
                        ? [{ label: "Heating", value: "heating" }]
                        : []),
                    ]}
                  value={selectedMode}
                  onChange={(value) => {
                    setSelectedMode(value);
                  }}
                />
            </span>
          </div>
          <div className="mb-4 snapshot-bradcrumb">
            <span className="breadcrumbs-title">
              {activeBuildingName}
              {activeBuildingName !== "" && "  >  "}
            </span>
            {getSelectedDeviceTitle(selectedDevice)}
          </div>
          <div className="grid-base grid3">
            <div className="c2">
              <h3>At-A-Glance</h3>
              <div className="grid-base grid2">
                <div className="d-flex right-border flex-column align-items-center justify-content-center">
                  <h2>
                    <span>Coil Fouling Level</span>
                    <Tooltip
                        className="ml-2"
                        id="my-tooltip-foul-lvl"
                        content={<p>Indicates the current fouling level of a coil where zero represents a recently cleaned coil and 100 or higher represents a coil requiring cleaning. Higher fouling levels indicate reduced coil performance and increased energy consumption as compared to a clean coil.</p>}
                    />
                  </h2>
                  <div>
                    <CircularDonutChart
                        selectedValue={recentCoilActiveList?.fouling_level}
                        maxValue={10}
                        textColor="#000"
                        valueFontSize={72}
                        radius={90}
                        strokeWidth={20}
                        activeStrokeColor="#0f4fff"
                        inactiveStrokeColor="#FFF"
                        backgroundColor="#F4F4F4"
                        withGradient
                        anticlockwise={false}
                    />
                  </div>

                  {getMonthsToNextCleaning() !== undefined && (
                    <NextCleaningSection
                      monthsToNextCleaning={getMonthsToNextCleaning()}
                      enercostpenAvgPre={recentCoilActiveList?.enercostpenAvgPre}
                      enercostpenSum={recentCoilActiveList?.enercostpenSum}
                    />
                  )}

                </div>
                <div className="d-flex flex-column align-items-center justify-content-start">
                  {getStatus(recentCoilActiveList?.smartcoil_state)}
                  <div className="gateway justify-content-center align-items-center">
                    <SmartCoilImage
                      color={getSmartCoilColor(recentCoilActiveList?.fouling_level)}
                    />
                  </div>
                  <hr />
                  <h4>
                    {recentCoilActiveList?.events
                      ? "Event Tracker"
                      : ""}
                  </h4>
                  <div className="grid2 event-tracker">
                    <table>
                      <tbody>
                        {recentCoilActiveList?.events?.map((item, index) => {
                          if (index <= 2 && item?.value !== null) {
                            return (
                              <tr key={item?.value + index}>
                                <td style={{minWidth: '100px'}}>{item?.value?.replaceAll('_', ' ')}</td>
                                <td>
                                  {moment(new Date(parseInt(item?.ts))).format(
                                    "MMMM DD, YYYY"
                                  )}
                                </td>
                                <td>{moment(item?.ts).format("hh:mm A")}</td>
                              </tr>
                            );
                          }
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <PerformanceMetrics
              selectedMode={selectedMode}
              selectedDevice={selectedDevice}
              setLoading={setPerfMetricsLoading}
            />
            <div className="c3 grid-green">
              <h3>How are you doing?</h3>
              <div className="fancy-boxes ">
                {howDoingData &&
                  howDoingData?.map((item, index) => (
                    <div key={item?.id}>
                      <div className="d-flex justify-content-between">
                        <span className="colored">{item?.title}</span>
                      </div>
                      <div className="text-900 font-normal text-4xl flex justify-content-center" style={{minHeight: '47.25px'}}>
                        {
                          recentCoilActiveList?.smartcoil_state !== RUNNING_STATE
                              ? <></>
                              :
                          index === 0
                          ? recentCoilActiveList?.energy_saving == null
                            ? "N/A"
                            : parseInt(
                                recentCoilActiveList?.energy_saving
                              )?.toLocaleString("en-US")
                          : index === 1
                          ? recentCoilActiveList?.cost_saving == null
                            ? "N/A"
                            : "$" +
                              parseInt(
                                recentCoilActiveList?.cost_saving
                              )?.toLocaleString("en-US")
                          : recentCoilActiveList?.carbon_offset == null
                          ? "N/A"
                          : (Math.round(Number(
                              recentCoilActiveList?.carbon_offset
                            ) * 10) / 10)?.toLocaleString("en-US")}
                      </div>
                      <div className="d-flex justify-content-center savings-text">
                        {item.valueSymbol}
                      </div>
                      <span
                        className="info-icon mb-1 mr-3"
                        type="button"
                        data-tooltip-id={"my-tooltip-" + index}
                      >
                        <span className="p-button-icon pi pi-info-circle"></span>
                      </span>
                      <ReactTooltip
                        id={"my-tooltip-" + index}
                        place="left"
                        style={{
                          zIndex: 9999,
                          width: "200px",
                          background: "black",
                        }}
                        content={item?.tooltip}
                      />
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomLoader
        idLoading={isLoading || isPerfMetricsLoading}
        handleLoading={() => setLoading(!isLoading && !isPerfMetricsLoading)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  activeList: state.coilList.activeList,
  activeSnapList: state.coilList.activeSnapList,
  buildingList: state.buildingList.buildingList,
  relationList: state.buildingList.relationList,
});
export default connect(mapStateToProps, { onlyActiveCoil, activeSnapCoil })(
  Snapshot
);
